import { cssMap as unboundedCssMap } from '@compiled/react';
import type { ComponentType, FC, ReactNode } from 'react';
import React, { useCallback, useRef } from 'react';
import type { WrappedComponentProps } from 'react-intl';

import { Box, Grid, Inline } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import NodeIcon from '@atlaskit/icon/core/node';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';

import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent.tsx';
import { isValidEmojiOrFavicon } from '@confluence/emoji-title/entry-points/isValidEmojiOrFavicon.ts';
import { ContentTreeIconLoader as ContentTreeIcon } from '@confluence/icons/entry-points/ContentTreeIcon.ts';
import type { ContentTreeTypes } from '@confluence/icons/entry-points/contentTreeTypes.ts';
import { includesValidContentTreeType } from '@confluence/icons/entry-points/contentTreeTypes.ts';
import type { IconItemType } from '@confluence/page-tree/entry-points/PageTreeAfterIcon.ts';
import { PageTreeAfterIcon } from '@confluence/page-tree/entry-points/PageTreeAfterIcon.ts';
import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';

type SpaceViewsListItemProps = {
	title: string;
	url: string;
	onItemClick?: (id: string, index?: number) => void;
	isSelected: boolean;
	item: IconItemType;
	spaceKey: string;
	isSuperAdmin: boolean;
	displayEmoji?: string;
	contentType?: string;
	closeAllHoverPageCards?: () => void;
	setOpenQuickActionsId: (state: string | null) => void;
	isQuickActionsOpen: boolean;
	isQuickActionsFocused: boolean;
	setFocusedQuickActionsId: (focusedQuickActionsId: string | null) => void;
	setIsEditingTitleId: (isEditingTitleId: string | null) => Promise<void>;
	onEnterHoverTarget?: (params: {
		itemID: string;
		isQuickActionsFocused: boolean;
		hoverPageCardTriggerRef: React.RefObject<HTMLDivElement>;
	}) => void;
	onLeaveHoverTarget?: () => void;
	onBlurHoverTarget?: (e) => void;
	shouldRenderAfterIcon?: boolean;
	index?: number;
	renderTitle?: ({ title }: { title: string }) => ReactNode;
};

const styles = cssMap({
	emojiWrapper: {
		display: 'flex',
		flexShrink: 0,
		alignItems: 'center',
		marginRight: token('space.075'),
	},
	nav4EmojiWrapper: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexShrink: 0,
	},
	title: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	contentIconWrapper: {
		display: 'flex',
		alignItems: 'center',
		paddingRight: token('space.075'),
	},
	contentIconWrapperNav4: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexShrink: 0,
	},
	grid: {
		alignItems: 'center',
		columnGap: token('space.075'),
		gridTemplateColumns: 'minmax(0, auto) 1fr',
	},
});

const EmojiOrContentIcon = ({ isContentTreeIconVisible, contentType, iconColor, displayEmoji }) => {
	if (isContentTreeIconVisible && !displayEmoji) {
		return (
			<Box as="span" xcss={styles.contentIconWrapperNav4}>
				<ContentTreeIcon type={contentType as ContentTreeTypes} label="" color={iconColor} />
			</Box>
		);
	} else if (isValidEmojiOrFavicon(displayEmoji, contentType)) {
		return (
			<LazyEmojiComponentLoader
				emoji={displayEmoji}
				height={16}
				context="spaceViews"
				wrapper={Nav4EmojiWrapper}
				contentType={contentType}
			/>
		);
	} else {
		return null;
	}
};

const quickActionsMenuDisplayStylesNav4 = unboundedCssMap({
	displayAndOpacity: {
		'--display-after-icon': 'none',
		'--opacity-after-icon': '0%',
		'&:hover, &:focus-within': {
			'--display-after-icon': 'flex',
			'--opacity-after-icon': '100%',
		},
	},
	isQuickActionsFocused: {
		'--display-after-icon': 'flex',
		'--opacity-after-icon': '100%',
	},
	isForceHoverBackground: {
		'--display-after-icon': 'flex',
		'--opacity-after-icon': '100%',
	},
});

const Nav4EmojiWrapper: ComponentType<{ children?: React.ReactNode }> = ({ children }) => {
	return (
		<Inline as="span" xcss={styles.nav4EmojiWrapper}>
			{children}
		</Inline>
	);
};

export const SpaceViewsListItem: FC<SpaceViewsListItemProps & WrappedComponentProps> = ({
	title,
	url,
	onItemClick,
	isSelected,
	item,
	displayEmoji = '',
	contentType,
	spaceKey,
	isSuperAdmin = false,
	closeAllHoverPageCards = () => {},
	setOpenQuickActionsId,
	isQuickActionsOpen,
	isQuickActionsFocused,
	setFocusedQuickActionsId,
	setIsEditingTitleId,
	onEnterHoverTarget = () => {},
	onLeaveHoverTarget = () => {},
	onBlurHoverTarget = () => {},
	shouldRenderAfterIcon,
	index,
	renderTitle = ({ title }) => title,
}) => {
	const hoverPageCardTriggerRef = useRef<HTMLDivElement>(null);

	const onClick = useCallback(() => {
		onItemClick && onItemClick(item.id, index);
		closeAllHoverPageCards();
	}, [onItemClick, item.id, index, closeAllHoverPageCards]);

	const iconColor = isSelected ? token('color.icon.selected') : undefined;

	const isContentTreeIconVisible = contentType && includesValidContentTreeType(contentType);
	const isLiveDoc = contentType === 'page' && item.data.subType === 'live';
	const iconContentType = isLiveDoc ? 'liveDoc' : contentType;

	return (
		<div
			ref={hoverPageCardTriggerRef}
			onBlur={onBlurHoverTarget}
			onMouseEnter={() =>
				onEnterHoverTarget({
					itemID: item.id.toString(),
					isQuickActionsFocused,
					hoverPageCardTriggerRef,
				})
			}
			onMouseLeave={onLeaveHoverTarget}
			onFocus={() =>
				onEnterHoverTarget({
					itemID: item.id.toString(),
					isQuickActionsFocused,
					hoverPageCardTriggerRef,
				})
			}
			css={[
				quickActionsMenuDisplayStylesNav4.displayAndOpacity,
				isQuickActionsOpen && quickActionsMenuDisplayStylesNav4.isForceHoverBackground,
				isQuickActionsFocused && quickActionsMenuDisplayStylesNav4.isQuickActionsFocused,
			]}
		>
			<HoverPreloader
				name="space-views-list-item"
				preloadUrl={url}
				preloadOptions={PreloadingPresets.onlyJS}
			>
				<LinkMenuItem
					testId="space-views-list-item"
					onClick={onClick}
					href={url}
					isSelected={isSelected}
					elemBefore={<NodeIcon label="" />}
					elemAfter={
						shouldRenderAfterIcon && (
							<PageTreeAfterIcon
								setFocusedQuickActionsId={setFocusedQuickActionsId}
								isSuperAdmin={isSuperAdmin}
								isQuickActionsOpen={isQuickActionsOpen}
								setOpenQuickActionsId={setOpenQuickActionsId}
								setIsEditingTitleId={setIsEditingTitleId}
								closeAllHoverPageCards={closeAllHoverPageCards}
								spaceKey={spaceKey}
								item={item}
								displayTitle={title}
								hideFolder
								disableRename
							/>
						)
					}
				>
					<Grid xcss={styles.grid}>
						<EmojiOrContentIcon
							isContentTreeIconVisible={isContentTreeIconVisible}
							contentType={iconContentType}
							iconColor={iconColor}
							displayEmoji={displayEmoji}
						/>
						<Box as="span" xcss={styles.title}>
							{renderTitle({ title })}
						</Box>
					</Grid>
				</LinkMenuItem>
			</HoverPreloader>
		</div>
	);
};
